import { useState, useEffect, useRef } from 'react'
import { formatElapsedTime } from '../utils/getElapsedTime'

const ONE_SECOND = 1000
const DEFAULT_TIME = '--h --m --s'

interface UseChargingTimerParams {
  startTime?: string | null
  endTime?: string | null
  serverNow?: string
}

export const useChargingTimer = ({ startTime, endTime, serverNow }: UseChargingTimerParams) => {
  const [elapsedTime, setElapsedTime] = useState<string>(DEFAULT_TIME)

  // `serverNow` fica num ref de proposito, fora das dependencias do efeito.
  // Cada notificacao da sessao traz um valor novo, e reancorar o cronometro em
  // todas elas fazia o tempo andar para tras: `server_time_now` e carimbado
  // quando a task do Celery roda, nao quando a notificacao chega no aparelho,
  // entao a latencia de entrega (que varia) entrava direto na conta. Duas
  // notificacoes com latencias diferentes = salto visivel na tela.
  const serverNowRef = useRef(serverNow)
  serverNowRef.current = serverNow ?? serverNowRef.current

  useEffect(() => {
    if (!startTime) {
      setElapsedTime(DEFAULT_TIME)
      return
    }

    const startTimeMs = new Date(startTime).getTime()

    const endTimeMs = endTime ? new Date(endTime).getTime() : NaN
    if (!isNaN(endTimeMs)) {
      setElapsedTime(formatElapsedTime(Math.max(0, endTimeMs - startTimeMs)))
      return
    }

    const firstServerNow = serverNowRef.current
    const clockOffset = firstServerNow ? new Date(firstServerNow).getTime() - Date.now() : 0

    let lastElapsed = 0

    const updateTimer = () => {
      const elapsed = Date.now() + clockOffset - startTimeMs
      lastElapsed = Math.max(lastElapsed, elapsed, 0)
      setElapsedTime(formatElapsedTime(lastElapsed))
    }

    updateTimer()
    const interval = setInterval(updateTimer, ONE_SECOND)

    return () => clearInterval(interval)
  }, [startTime, endTime])

  return { elapsedTime: startTime ? elapsedTime : '' }
}
